import { useId, useState } from 'react'
import type { Instrument } from '../api/types'
import {
  filterInstruments, instrumentLabel, instrumentTypes, orderInstruments, toggleInstrument,
} from '../lib/instruments'
import { relativeAge } from '../lib/time'

interface Props {
  instruments: Instrument[]
  selected: number[]
  onChange: (instrumentIds: number[]) => void
}

/** The station's sensors, each with a tick: what is ticked is what the page
 *  shows, charted together and read side by side. 25 rows at Zhinvali, where
 *  the parameter list it replaces had over a thousand, so a filter sits on
 *  top and "all" and "none" act on what the filter shows.
 *
 *  Each row says how long ago the node last reported anything, in words: a
 *  node that went quiet is the first thing a reader should be able to see,
 *  and colour is not available for it (DESIGN_GUIDELINE §2.6). */
export function InstrumentList({ instruments, selected, onChange }: Props) {
  const [query, setQuery] = useState('')
  const [type, setType] = useState('')
  const baseId = useId()
  const filterId = `${baseId}-filter`
  const typeId = `${baseId}-type`
  const types = instrumentTypes(instruments)
  const shown = filterInstruments(instruments, query, type)
  const filtering = Boolean(query.trim() || type)
  const ticked = new Set(selected)
  const shownIds = shown.map((i) => i.instrument_id)

  const set = (ids: Iterable<number>) => {
    const want = new Set(ids)
    onChange(orderInstruments(instruments)
      .filter((i) => want.has(i.instrument_id)).map((i) => i.instrument_id))
  }
  const allShown = shownIds.length > 0 && shownIds.every((id) => ticked.has(id))
  const noneShown = shownIds.every((id) => !ticked.has(id))

  return (
    // A group of checkboxes, not navigation: ticking one changes the page
    // in place rather than going somewhere.
    <div className="instruments" role="group" aria-label="Sensors">
      <h2 className="instruments-title">Filter Instruments</h2>
      <label className="visually-hidden" htmlFor={filterId}>Filter sensors</label>
      <input id={filterId} type="search" className="instrument-filter"
             placeholder="Filter by name, model or id" value={query}
             onChange={(event) => setQuery(event.target.value)} />
      {/* Only where there is a choice: the types are the models the gateway
          reports, nothing named here that the payload does not carry. */}
      {types.length > 1 && (
        <label className="field instrument-type" htmlFor={typeId}>
          <span>Sensor type</span>
          <select id={typeId} value={type} onChange={(event) => setType(event.target.value)}>
            <option value="">All types</option>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
      )}
      <div className="instrument-filter-foot">
        <small className="muted" aria-live="polite">
          {filtering ? `${shown.length} of ${instruments.length} shown` : `${instruments.length} sensors`}
        </small>
        {filtering && (
          <button type="button" className="link"
                  onClick={() => { setQuery(''); setType('') }}>
            Clear
          </button>
        )}
      </div>

      <div className="field-head">
        <span>Sensors</span>
        <span className="picks">
          <button type="button" className="link" disabled={allShown}
                  onClick={() => set([...selected, ...shownIds])}>
            all
          </button>
          <button type="button" className="link" disabled={noneShown}
                  onClick={() => set(selected.filter((id) => !shownIds.includes(id)))}>
            none
          </button>
        </span>
      </div>
      <small className="muted instrument-count" aria-live="polite">
        {selected.length} of {instruments.length} selected
      </small>

      {shown.length === 0 && (
        <p className="muted instrument-empty">
          {query.trim() ? `No sensor matches “${query.trim()}”.` : 'No sensor of this type.'}
        </p>
      )}
      <ul>
        {shown.map((i) => {
          const on = ticked.has(i.instrument_id)
          const label = instrumentLabel(i)
          const nameId = `${baseId}-${i.instrument_id}-name`
          const metaId = `${baseId}-${i.instrument_id}-meta`
          return (
            <li key={i.instrument_id} className={on ? 'instrument on' : 'instrument'}>
              <label className="instrument-check">
                {/* Named by the sensor alone, the rest as its description:
                    "last reported 5 min ago" changes every minute, and a
                    screen reader would otherwise read a new name each time. */}
                <input type="checkbox" checked={on}
                       aria-labelledby={nameId} aria-describedby={metaId}
                       onChange={() => onChange(
                         toggleInstrument(instruments, selected, i.instrument_id))} />
                <span className="tick" aria-hidden="true" />
                <span className="instrument-text">
                  <span className="instrument-name" id={nameId}>{label}</span>
                  <span id={metaId}>
                    <small className="muted">
                      {[i.model, String(i.instrument_id)].filter(Boolean).join(' · ')}
                    </small>
                    {/* The lines are blocks on screen; a screen reader reads
                        the description as one run and needs the gap. */}
                    {' '}
                    <small className="muted">
                      {i.last_seen ? `last reported ${relativeAge(i.last_seen)}`
                                   : 'no readings'}
                    </small>
                  </span>
                </span>
              </label>
              {/* Back to one sensor in one click, rather than unticking the
                  other eleven. Its own button, outside the label, so it
                  never toggles the row it sits on. */}
              <button type="button" className="link only"
                      aria-label={`Show only ${label}`}
                      onClick={() => onChange([i.instrument_id])}>
                only
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
